import type { PayRateUnit, TeachingRole } from './payroll';
import type { PricingType, BillingPeriodKind, EnrollmentMode } from './student';

export type SlotStatus = 'ACTIVE' | 'ARCHIVED';
export type SessionStatus = 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
export type AttendanceStatus = 'PENDING' | 'PRESENT' | 'ABSENT' | 'EXCUSED';

/** The `?branchId=` value that means the "Chưa gán chi nhánh" scope: slots that
 *  EXIST but carry no branch. Branch ids are cuids (`c…`), so this literal can
 *  never collide with one. It is deliberately a plain string, not a new type:
 *  `branchStore.branchId` stays `string | null` and the menu is the only thing
 *  that knows what it means.
 *
 *  Mirrored (not imported) as a bare literal in `packages/validation`'s
 *  `branchFilterSchema` — that package deliberately does not depend on this one
 *  (same reason as `packages/validation/src/invoice.ts:90`). */
export const UNASSIGNED_BRANCH = 'none';

export interface BranchDto {
  id: string;
  organizationId: string;
  name: string;
  /** ISO instant, or null for a live branch. */
  archivedAt: string | null;
  /** Slots (any status) currently pointing at this branch. Drives the settings
   *  list's "n lớp" line and nothing else. */
  slotCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BranchListDto {
  branches: BranchDto[];
  /** ACTIVE slots in this org with `branchId: null`. Drives the switcher's
   *  attention badge, the settings strip and the adopt dialog's {{count}} —
   *  and it is exactly the set both assign endpoints move, so the number the
   *  teacher was shown is the number that moved.
   *
   *  Deliberately ACTIVE-only, unlike `BranchDto.slotCount` (any status): the
   *  copy says "lớp hiện có" and an archived slot is invisible under every
   *  filter anyway, so counting it would promise a change the teacher cannot
   *  see. Assigning leaves archived slots branchless on purpose — archiving
   *  never rewrites history. */
  unassignedSlotCount: number;
}

export interface ClassSlot {
  id: string;
  organizationId: string;
  teacherId: string;
  subjectId: string;
  /** The branch this class is taught at, or null for "no branch yet" — which
   *  is a real, permanent state, not a migration artefact: a solo teacher
   *  never creates a branch at all. Null slots appear only under "Tất cả". */
  branchId: string | null;
  dayOfWeek: number; // 1=Mon..7=Sun
  startTime: string; // "HH:mm"
  durationMin: number;
  maxCapacity: number | null;
  name: string | null;
  status: SlotStatus;
  generatedUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One of the OTHER teachers on a slot (the đứng-tên teacher travels on
 *  `ClassSlot.teacherId`). One exception the FE must handle: a row whose
 *  membershipId EQUALS the slot's teacherId is an override carrier, not a
 *  second teacher — merge its `rateOverride` into the đứng-tên row and never
 *  render it separately (see the API's serializeSlotDetail). */
export interface SlotTeacherRow {
  membershipId: string;
  role: TeachingRole;
  sortOrder: number;
  /** Per-slot tiền công for this teacher. Optional: the API omits the field
   *  entirely for a caller without `manage:members` — a MEMBER-role teacher
   *  sees colleagues' NAMES but never their money. */
  rateOverride?: number | null;
  /** Unit for `rateOverride`, independent of the teacher's own unit. Only
   *  meaningful when `rateOverride` is set; null means "use the teacher's
   *  own rateUnit" — the pre-existing fallback. Same visibility gate as
   *  `rateOverride`. */
  rateOverrideUnit?: PayRateUnit | null;
}

/** One of the OTHER teachers on a materialized session. No rate of any kind
 *  — there is no session-level rate override, by design. */
export interface SessionTeacherRow {
  membershipId: string;
  role: TeachingRole;
  sortOrder: number;
  /** True when this person is still on the slot's CURRENT teacher list
   *  (đứng-tên or an `SlotTeacher` row) — false means they were added to
   *  THIS session only (a substitute or a one-off trợ giảng), which earns
   *  them the same "Đột xuất" badge treatment as an ad-hoc student. Always
   *  false for an ad-hoc session (`slotId === null`): there is no slot list
   *  to belong to. */
  fromSlot: boolean;
}

export interface SlotMember {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED';
}

export interface ClassSlotWithDetail extends ClassSlot {
  subject: { id: string; name: string };
  members: SlotMember[];
  memberCount: number;
  sessionCount: number;
  teachers: SlotTeacherRow[];
  /** THI-267: the ClassGroup (lớp) this slot belongs to. Required — every
   *  ClassSlot has a groupId at the DB level (Task 1's migration made it
   *  NOT NULL) and `serializeSlotDetail` fills this on every row (Task 6). */
  group: ClassGroupRef;
}

export type TeacherTimetableSlot = ClassSlotWithDetail;

// ── THI-267: the class layer (ClassGroup / "lớp") ───────────────────────────

/** How an org organizes students into classes (mirrors the DB enum
 *  `ClassMode`; this package does not depend on `@repo/db`, so it is
 *  re-declared as a literal union, same convention as `SlotStatus` above). */
export type ClassMode = 'COHORT' | 'INDIVIDUAL';

/** The eight colour keys a ClassGroup may carry (decision 7): OKLCH values
 *  live in the design tokens, not here. Hue 156 and 72 are excluded because
 *  they collide with existing status colours. */
export const CLASS_COLORS = ['rose', 'amber', 'lime', 'teal', 'sky', 'indigo', 'violet', 'pink'] as const;
export type ClassColor = (typeof CLASS_COLORS)[number];

/** Derived, never stored: `UPCOMING` before `startsOn`, `ENDED` once past
 *  (inclusive) `endsOn`, `RUNNING` otherwise. Computed server-side in one
 *  place alongside `displayName` (spec §4). */
export type ClassPhase = 'UPCOMING' | 'RUNNING' | 'ENDED';

/** The minimal pointer to a class a caller needs to render a chip or a link
 *  — never the full row. `color: null` means "automatic" (decision 7); it is
 *  the FE's job to resolve that to a rendered colour, not this type's. */
export interface ClassGroupRef {
  id: string;
  displayName: string;
  color: ClassColor | null;
}

/** One khung giờ as it appears inside a class row or detail. `nextSession` is
 *  present only on the detail response (`GET /api/classes/:id`, spec §4
 *  bullet 2) — the list response (bullet 1) omits the field entirely rather
 *  than sending it as `null`, since computing it for every slot of every row
 *  of a list is the cost the detail endpoint alone should pay. */
export interface ClassSlotRow {
  id: string;
  dayOfWeek: number; // 1=Mon..7=Sun
  startTime: string; // "HH:mm"
  durationMin: number;
  teacherId: string;
  branchId: string | null;
  nextSession?: { id: string; startAt: string; attendanceMarked: boolean } | null;
}

/** Row of `GET /api/classes` (spec §4 bullet 1). `name` is the raw stored
 *  value (nullable); `displayName` is always present, derived server-side in
 *  one place from `name` when set, else from the slots' subject/weekday
 *  pattern. `rosterCount` counts distinct live enrollments across every
 *  slot of the class, not a sum over slots (a student in two khung giờ of
 *  the same class counts once). */
export interface ClassGroupListRow {
  id: string;
  name: string | null;
  displayName: string;
  subject: { id: string; name: string };
  color: ClassColor | null;
  slots: ClassSlotRow[];
  /** Distinct teacher ids across the class's slots. */
  teacherIds: string[];
  rosterCount: number;
  maxCapacity: number | null;
  /** ISO instant — UTC-day (§2.5). */
  startsOn: string;
  /** ISO instant — UTC-day, inclusive; null = open-ended. */
  endsOn: string | null;
  phase: ClassPhase;
  /** Stored status: ARCHIVED classes live in the Đã lưu trữ bucket and
   *  offer Khôi phục; `phase` is derived from the window and is orthogonal. */
  status: 'ACTIVE' | 'ARCHIVED';
}

/** One roster member of `GET /api/classes/:id` (spec §4 bullet 2). `joinedAt`
 *  is the EARLIEST live membership's `joinedAt` across the class's own
 *  slots — a student who joined slot A in July and slot B in August shows
 *  July here. `slotIds` lists only the LIVE (not left) memberships within
 *  this class's own slots, which is what drives the "2/3 khung giờ" chip
 *  math client-side. A `PAUSED` enrollment is included with `status:
 *  'PAUSED'` rather than filtered out (spec: "paused ones flagged"). */
export interface ClassRosterRow {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  nickname: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED';
  joinedAt: string;
  pricing: {
    pricingType: PricingType;
    rate: number;
    billingPeriod: BillingPeriodKind;
    /** Mirrors `Enrollment.chargeAbsent` — whether a no-notice absence bills. */
    chargeAbsent: boolean;
    /** PER_SESSION TERM only: the term size, for "100.000đ/20 buổi". */
    termSize: number | null;
  };
  slotIds: string[];
  /** The live memberships behind `slotIds`, with each one's own `joinedAt`
   *  (ISO instant; a "from today" join stores the real instant, §1.15). */
  memberships: { slotId: string; joinedAt: string }[];
}

/** Response of `GET /api/classes/:id`: the list row plus the full roster.
 *  `slots` here is the SAME `ClassSlotRow[]` as the list row, but every
 *  element now carries `nextSession` (see `ClassSlotRow`'s own comment). */
export interface ClassGroupDetail extends ClassGroupListRow {
  roster: ClassRosterRow[];
  /** Phòng / ghi chú (spec §3). */
  room: string | null;
  /** The class's money DEFAULTS (spec §2 decision 4): what a new member's
   *  enrollment is prefilled from. Never the enrollment itself. Every field
   *  nullable = "fall back to the subject / org default". */
  defaults: {
    pricingType: PricingType | null;
    billingPeriod: 'MONTHLY' | 'TERM' | null;
    termSize: number | null;
    ratePerSession: number | null;
    monthlyRate: number | null;
    chargeAbsent: boolean | null;
  };
}

/** One class chip on a student's card or profile (spec §4, the students-list
 *  and student-page `classes[]` fields): `joinedSlots` / `totalSlots` drive
 *  the "(2/3)" partial-membership text. `color: null` is the same
 *  "automatic, not yet resolved to a colour" state as `ClassGroupRef.color`
 *  — a single-slot class has none. */
export interface StudentClassChip {
  id: string;
  displayName: string;
  color: ClassColor | null;
  joinedSlots: number;
  totalSlots: number;
}

/** Response of POST /api/slots/move — a batch reassignment between two slots
 *  in one transaction, one shared instant. */
export interface MoveSlotMembersResult {
  moved: number;
  from: string;
  to: string;
}

export interface SessionParticipant {
  id: string;
  sessionId: string;
  enrollmentId: string;
  studentId: string;
  studentName: string;
  /** Tên thường gọi — mirrors `Student.nickname`. Required (not optional) so a
   *  caller can never forget to thread it through a new constructor: matches
   *  every match/display surface expecting it, alongside `name`, everywhere a
   *  teacher searches for a student (THI-82). */
  studentNickname: string | null;
  attendance: AttendanceStatus;
  markedAt: string | null;
  markedById: string | null;
  attendanceNote: string | null;
  fromSlot?: boolean;
  /** Saved EXCUSED marks for this enrollment within the session's own
   *  calendar month, excluding this session's own mark (self-exclusion by
   *  subtraction — see `apps/api/src/routes/sessions.ts`). Advisory only:
   *  nothing blocks marking past `Organization.settings.maxExcusedPerMonth`. */
  excusedThisMonth: number;
  /** Ad-hoc (`fromSlot === false`) `PRESENT` marks for this enrollment within
   *  the session's own calendar month, excluding this session's own mark
   *  (self-exclusion by subtraction, mirroring `excusedThisMonth` — see
   *  `apps/api/src/routes/sessions.ts`). Sessions with status `CANCELLED` are
   *  excluded. This is the raw made-up count: `excusedThisMonth` keeps its
   *  own exact meaning and is NOT netted against this here — netting
   *  (`net = max(0, excused − madeUp)`) happens in the FE display lib. */
  madeUpThisMonth: number;
  /** Mirrors `Enrollment.chargeAbsent` — whether a no-notice absence bills. */
  chargeAbsent: boolean;
  /** Mirrors `Enrollment.pricingType`. */
  pricingType: PricingType;
  /** Mirrors `Enrollment.billingPeriod`. */
  billingPeriod: BillingPeriodKind;
  /** True when this enrollment is PER_SESSION + MONTHLY and a RECONCILED
   *  MONTHLY billing period exists for this session's calendar month —
   *  computed with exactly the predicate `POST /:id/attendance`'s I5 guard
   *  uses (see `apps/api/src/routes/sessions.ts`), so the UI can never
   *  disagree with the 409 it would get back from a save attempt. TERM and
   *  MONTHLY_FLAT enrollments are never locked (documented gap — see the I5
   *  guard's own comment). */
  attendanceLocked: boolean;
  /** The `'YYYY-MM'` label of the RECONCILED period that locked this
   *  participant, or `null` when `attendanceLocked` is false. */
  lockedPeriodLabel: string | null;
  /** How this student pays — `enrollmentModeOf(enrollment)`, the same three
   *  buckets the Học viên filter chips and the enrollment form name. Always
   *  present. */
  mode: EnrollmentMode;
  /**
   * 1-based position THIS session occupies in the currently open khoá — "buổi
   * thứ 7". Non-null only for `mode: 'SESSION_TERM'` with an OPEN term period.
   *
   * Deliberately STABLE across marking: it is `TermState.consumed + 1` while
   * this session's own mark is not yet inside `consumed`, and `consumed`
   * itself once it is — so tapping "Có mặt" does not make the number jump.
   * May exceed `termSize` on an over-run, mirroring `TermState.remaining`,
   * which is documented as unclamped for the same reason.
   */
  termIndex: number | null;
  /** The open khoá's size ("… trong khoá 8 buổi"). Non-null under exactly the
   *  same condition as `termIndex` — no open term means neither number is
   *  meaningful, and the badge falls back to a bare "Khoá". */
  termSize: number | null;
}

export interface ClassSession {
  id: string;
  organizationId: string;
  slotId: string | null;
  teacherId: string;
  subjectId: string;
  startAt: string;
  durationMin: number;
  status: SessionStatus;
  editedManually: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SessionWithDetail extends ClassSession {
  subject: { id: string; name: string };
  participants: SessionParticipant[];
  teachers: SessionTeacherRow[];
  /** The session's SLOT's branch (there is no branch column on ClassSession
   *  itself). Null for an ad-hoc session, which is why a branch filter
   *  excludes it. */
  branchId: string | null;
  /** Same `fromSlot` idea as `SessionTeacherRow`, for the GV chịu trách
   *  nhiệm (`teacherId`) instead of an entry in `teachers`: true when they
   *  are still the slot's own teacherId. False on an ad-hoc session
   *  (`slotId === null`) or once the slot has swapped to someone else. */
  teacherFromSlot: boolean;
}

/** Row of GET /api/schedule-templates. */
export interface ScheduleTemplateSummary {
  id: string;
  name: string;
  /** ISO instant of the last apply, or null if never applied. Display-only. */
  appliedAt: string | null;
  entryCount: number;
  updatedAt: string;
}

/** Why an entry will be skipped at apply time. Null = healthy.
 *  `SUBJECT_MISMATCH` — the enrollment's subject differs from the slot's —
 *  is a data-integrity problem that `PUT /:id/entries` rejects at write
 *  time; it exists here to flag a row that predates that guard (or was
 *  written directly), so GET /:id and the diff can still name it rather
 *  than crash or silently apply a nonsensical join. */
export type ScheduleTemplateEntryProblem = 'SLOT_ARCHIVED' | 'ENROLLMENT_INACTIVE' | 'SUBJECT_MISMATCH';

export interface ScheduleTemplateEntryDetail {
  slotId: string;
  enrollmentId: string;
  /** Null when the enrollment no longer exists at all (hard orphan). */
  studentId: string | null;
  studentName: string | null;
  /** "T4 18:00 · Piano" — pre-composed server-side so the warning chip and
   *  the apply dialog name the slot identically without the client having
   *  to hold an archived slot it never fetched. Null for a hard orphan. */
  slotLabel: string | null;
  /** Mirrors Enrollment.status so the card can render a PAUSED member the
   *  same way the real roster does. */
  enrollmentStatus: 'ACTIVE' | 'PAUSED' | 'ENDED' | null;
  orphaned: boolean;
  problem: ScheduleTemplateEntryProblem | null;
}

export interface ScheduleTemplateDetail extends ScheduleTemplateSummary {
  entries: ScheduleTemplateEntryDetail[];
}

/** The one hard guard: applying while a previous month is still OPEN would
 *  rewrite the classification of last month's marked sessions. */
export interface OpenPeriodBlock {
  /** "YYYY-MM" — the month that has not been closed. */
  periodLabel: string;
  /** "YYYY-MM" — the month to type into chốt sổ to reconcile it
   *  (periodLabel's successor; closing M reconciles M−1). */
  closeLabel: string;
  /** How many enrollments hold an OPEN period at or before periodLabel. */
  enrollmentCount: number;
}

export interface ScheduleTemplatePreview {
  joins: { slotId: string; enrollmentId: string; studentName: string; slotLabel: string }[];
  leaves: { slotId: string; enrollmentId: string; studentName: string; slotLabel: string }[];
  unchanged: number;
  /** One row per distinct slot label among the skipped (orphaned) entries —
   *  not only `SLOT_ARCHIVED` rows: an `ENROLLMENT_INACTIVE` or
   *  `SUBJECT_MISMATCH` entry on an otherwise-healthy ACTIVE slot is grouped
   *  here too, under that slot's own label, per `ScheduleTemplateEntryProblem`. */
  skippedOrphans: { slotLabel: string; count: number }[];
  blockedByOpenPeriod: OpenPeriodBlock | null;
}

/** Response of POST /api/schedule-templates/:id/apply — what was actually
 *  executed, recomputed server-side inside the transaction. */
export interface ScheduleTemplateApplyResult {
  joined: number;
  left: number;
  unchanged: number;
  skippedOrphans: number;
  appliedAt: string;
  /** Name of the auto-backup template snapshotted just before this apply's
   *  diff executed — null when the diff was empty (a no-op apply backs up
   *  nothing, since there is nothing at risk of being overwritten). */
  backupTemplateName: string | null;
}
